import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Title,
} from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1CodeInput,
  V1CodeOutput,
  V1ValueType,
} from '@/generated/hooks/grimoire/models';
import { BooleanInput } from '@/modules/automation-inputs/components/BooleanInput';
import { ComplexInput } from '@/modules/automation-inputs/components/ComplexInput';
import { DateInput } from '@/modules/automation-inputs/components/DateInput';
import { DateTimeInput } from '@/modules/automation-inputs/components/DateTimeInput';
import { EnumInput } from '@/modules/automation-inputs/components/EnumInput';
import { FileInput } from '@/modules/automation-inputs/components/file-upload/FileInput';
import { ListInput } from '@/modules/automation-inputs/components/ListInput';
import { NumberInput } from '@/modules/automation-inputs/components/NumberInput';
import { TextInput } from '@/modules/automation-inputs/components/TextInput';
import { TimeInput } from '@/modules/automation-inputs/components/TimeInput';
import type { InputComponentType } from '@/modules/automation-inputs/types';
import { processInputSpec } from '@/modules/automation-inputs/utils/inputProcessing';
import {
  getComplexTypeHint,
  getEnumMembers,
} from '@/modules/automation-inputs/utils/typeDetection';
import {
  listToCommonValue,
  toCommonValue,
} from '@/modules/automation-inputs/utils/valueConversion';
import { ValueChip } from '@/modules/outputs/components/ValueChip';
import type { TResolvedValue } from '@/modules/outputs/types';

import { formatValueType } from './manifest-value-type';

/** Rows shown before the table collapses behind a "Show more" toggle — past
 *  this, a wide schema (e.g. a test automation with a dozen inputs) would
 *  otherwise push the Steps section far down the page before ever reaching
 *  it. Same idea as `ManifestClampedText`'s length clamp, applied to rows
 *  instead of characters. */
const DEFAULT_VISIBLE_ROWS = 5;
const EMPTY_INPUT_KEYS: string[] = [];

interface IManifestIOTableProps {
  title: string;
  items: (V1CodeInput | V1CodeOutput)[];
  /** Uses the draft document treatment: type sits beneath the name and the
   *  table follows the compact three-column handoff. The default keeps the
   *  existing published/markdown-adjacent layout unchanged. */
  variant?: 'default' | 'document';
  /** Keeps the document's Value column visible before a focused run exists. */
  showValueColumn?: boolean;
  /** Whether the surrounding document renders its sticky title bar. The
   *  `document` variant's header sticks below that bar; without one it pins to
   *  the scroll container's own top edge instead of leaving a strip of
   *  scrolling content above itself. Ignored by the default variant. */
  hasTitleBar?: boolean;
  /**
   * Resolves a focused test run's real value for a given input/output name.
   * Omitted in the plain authoring view; when provided, adds a Value column
   * so the run's actual data sits directly on the same row as its schema
   * instead of a second, separate "Inputs"/"Outputs" block elsewhere.
   */
  getValue?: (name: string) => TResolvedValue | undefined;
  inputValues?: Record<string, Commonv1Value>;
  onInputValuesChange?: (values: Record<string, Commonv1Value>) => void;
  defaultInputKeys?: string[];
  onToggleDefault?: (name: string) => void;
  inputsReadOnly?: boolean;
}

/** The editable cell's own props. The value-editing fields are optional on
 *  `IManifestIOTableProps` (the published view omits them) but required here,
 *  so they are derived from the table's props rather than restated. */
interface IDocumentInputValueProps
  extends Required<
    Pick<
      IManifestIOTableProps,
      | 'inputValues'
      | 'onInputValuesChange'
      | 'defaultInputKeys'
      | 'inputsReadOnly'
    >
  > {
  name: string;
  type: V1CodeInput['type'];
  onToggleDefault?: IManifestIOTableProps['onToggleDefault'];
}

function DocumentInputValue({
  name,
  type,
  inputValues,
  onInputValuesChange,
  defaultInputKeys,
  onToggleDefault,
  inputsReadOnly,
}: IDocumentInputValueProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tInputs } = useTranslation('automation-inputs');
  const processed = processInputSpec(
    { name, type: type ?? { scalar_type: 'VALUE_SCALAR_TYPE_TEXT' } },
    inputValues,
  );
  const {
    baseType,
    componentType,
    displayValue,
    elementComponentType,
    elementType,
    isMultipleFiles,
    listItems,
    required,
  } = processed;
  const isPinned = defaultInputKeys.includes(name);
  const editorRef = useRef<HTMLDivElement>(null);
  // Set when the user tries to pin an input that has no value yet. Derived
  // rather than stored so typing a value clears it without an effect.
  const [hasAttemptedEmptyDefault, setHasAttemptedEmptyDefault] =
    useState(false);
  const isEmptyValue =
    displayValue === '' || displayValue === undefined || displayValue === null;
  const emptyDefaultError =
    hasAttemptedEmptyDefault && isEmptyValue
      ? tAutomation('automation.draftRail.inputs.emptyDefault')
      : undefined;

  const handleTogglePin = () => {
    // An empty input has no default to save, so the pin stays off until there
    // is a value. Flag the field and focus it so the value can be supplied
    // straight away.
    if (!isPinned && isEmptyValue) {
      setHasAttemptedEmptyDefault(true);
      requestAnimationFrame(() => {
        editorRef.current
          ?.querySelector<HTMLElement>(
            'input:not([type="hidden"]), textarea, select, [role="combobox"], button',
          )
          ?.focus();
      });
      return;
    }
    setHasAttemptedEmptyDefault(false);
    onToggleDefault?.(name);
  };
  const handleValueChange = (
    value: string | boolean,
    valueType: string = componentType,
  ) => {
    onInputValuesChange({
      ...inputValues,
      [name]: toCommonValue(value, valueType),
    });
  };

  const renderLeafEditor = ({
    controlName,
    controlType,
    value,
    valueType,
    onChange,
  }: {
    controlName: string;
    controlType: InputComponentType;
    value: string | boolean;
    valueType: V1ValueType;
    onChange: (next: string | boolean) => void;
  }) => {
    switch (controlType) {
      case 'number':
        return (
          <NumberInput
            name={controlName}
            value={String(value)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      case 'boolean':
        return (
          <BooleanInput
            name={controlName}
            label={name}
            checked={value === true || value === 'true'}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      case 'date':
        return (
          <DateInput
            name={controlName}
            value={String(value)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      case 'datetime':
        return (
          <DateTimeInput
            name={controlName}
            value={String(value)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      case 'time':
        return (
          <TimeInput
            name={controlName}
            value={String(value)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      case 'enum':
        return (
          <EnumInput
            name={controlName}
            value={String(value)}
            options={getEnumMembers(valueType)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
          />
        );
      default:
        return (
          <TextInput
            name={controlName}
            value={String(value)}
            onChange={onChange}
            disabled={inputsReadOnly}
            required={required}
            error={emptyDefaultError}
            placeholder={tAutomation(
              'automation.draftRail.inputs.setAtRunTime',
            )}
          />
        );
    }
  };

  const renderEditor = () => {
    if (componentType === 'file') {
      // No `isDefault`: that switches to the run sheet's override panel, which
      // needs the file history this table doesn't have and puts a second
      // upload affordance next to a file that is already attached.
      return (
        <FileInput
          compact
          name={name}
          value={String(displayValue)}
          onChange={value =>
            handleValueChange(value, isMultipleFiles ? 'fileList' : 'file')
          }
          multiple={isMultipleFiles}
          disabled={inputsReadOnly}
          required={required}
          singleColumn
        />
      );
    }
    if (componentType === 'list' && elementType && elementComponentType) {
      return (
        <ListInput
          name={name}
          items={listItems}
          disabled={inputsReadOnly}
          onChange={items =>
            onInputValuesChange({
              ...inputValues,
              [name]: listToCommonValue(items, elementType),
            })
          }
          renderElement={({ index, value, onChange }) =>
            renderLeafEditor({
              controlName: `${name}-${index}`,
              controlType: elementComponentType,
              value,
              valueType: elementType,
              onChange: next => onChange(String(next)),
            })
          }
        />
      );
    }
    if (componentType === 'complex') {
      return (
        <ComplexInput
          name={name}
          value={String(displayValue)}
          typeHint={getComplexTypeHint(baseType)}
          onChange={value => handleValueChange(value, 'complex')}
          disabled={inputsReadOnly}
          required={required}
        />
      );
    }
    if (componentType === 'none') {
      return (
        <Text level="small" color="muted">
          {tInputs('labels.noValueNeeded')}
        </Text>
      );
    }
    return renderLeafEditor({
      controlName: name,
      controlType: componentType,
      value: displayValue,
      valueType: baseType,
      onChange: handleValueChange,
    });
  };

  return (
    <Flex align="center" gap={2} width="full">
      <Flex direction="column" gap={1} className="min-w-0 flex-1">
        <div ref={editorRef} className="min-w-0">
          {renderEditor()}
        </div>
        {emptyDefaultError && (
          <Text level="xSmall" color="destructive">
            {emptyDefaultError}
          </Text>
        )}
      </Flex>
      {onToggleDefault && (
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          disabled={inputsReadOnly}
          aria-pressed={isPinned}
          aria-label={tAutomation(
            isPinned
              ? 'automation.draftRail.inputs.unpinInput'
              : 'automation.draftRail.inputs.pinInput',
            { name },
          )}
          onClick={handleTogglePin}
          // `bg-muted` was invisible as an on-state: in light mode --muted and
          // --accent are the same value, so a pinned input looked identical to
          // any hovered one. Borrow the primary tint instead, which reads as
          // "on" whether or not the pointer is over it.
          className={cn(
            'shrink-0',
            isPinned && 'border-primary/40 bg-primary/10 text-primary',
          )}
        >
          <Icon type="Pin" size="xs" />
        </Button>
      )}
    </Flex>
  );
}

/** Shared name/type/description(/value) table for a manifest's `inputs`/`outputs`.
 *  Collapses to `DEFAULT_VISIBLE_ROWS` rows when there are more than that,
 *  with a "Show more"/"Show less" toggle — reuses `ManifestClampedText`'s
 *  exact pattern and copy for consistency across the SOP view. */
export function ManifestIOTable({
  title,
  items,
  getValue,
  variant = 'default',
  showValueColumn = false,
  hasTitleBar = true,
  inputValues,
  onInputValuesChange,
  defaultInputKeys = EMPTY_INPUT_KEYS,
  onToggleDefault,
  inputsReadOnly = false,
}: IManifestIOTableProps) {
  const { t: tAutomation } = useTranslation('automation');
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const isLong = items.length > DEFAULT_VISIBLE_ROWS;
  const visibleItems =
    expanded || !isLong ? items : items.slice(0, DEFAULT_VISIBLE_ROWS);
  const isDocument = variant === 'document';
  const hasEditableInputs = Boolean(inputValues && onInputValuesChange);
  const hasValueColumn =
    Boolean(getValue) || showValueColumn || (isDocument && hasEditableInputs);

  return (
    <Flex direction="column" className="w-full min-w-0">
      <Flex
        align="center"
        className={cn(
          'sticky z-20 mb-2 w-full min-w-0 bg-card',
          isDocument ? 'h-8' : 'h-10',
          isDocument && hasTitleBar ? 'top-14' : 'top-0',
        )}
      >
        {isDocument ? (
          <Text
            level="xSmall"
            color="muted"
            weight="semibold"
            className="uppercase tracking-widest"
          >
            {title}
          </Text>
        ) : (
          <Title level="h3" className="font-medium">
            {title}
          </Title>
        )}
      </Flex>
      {/* X only: overflow-hidden clipped wide tables, and a Y scroller here
          would trap the sticky header above. */}
      <div className="w-full overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="divide-x bg-muted/40 hover:bg-muted/40">
              <TableHead className={cn(isDocument && 'w-44 px-4 py-2.5')}>
                {tAutomation('automation.manifestSopView.io.name')}
              </TableHead>
              {!isDocument && (
                <TableHead>
                  {tAutomation('automation.manifestSopView.io.type')}
                </TableHead>
              )}
              <TableHead className={cn(isDocument && 'px-4 py-2.5')}>
                {tAutomation('automation.manifestSopView.io.description')}
              </TableHead>
              {hasValueColumn && (
                <TableHead className={cn(isDocument && 'w-56 px-4 py-2.5')}>
                  {tAutomation('automation.manifestSopView.io.value')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.map((item, index) => {
              const value = item.name ? getValue?.(item.name) : undefined;
              return (
                <TableRow className="divide-x" key={item.name || index}>
                  <TableCell
                    className={cn('align-top', isDocument && 'px-4 py-3')}
                  >
                    <Flex direction="column" align="start" gap={1}>
                      <Text type="inlineCode" level="small">
                        {item.name}
                      </Text>
                      {isDocument && (
                        <Text level="xSmall" color="muted">
                          {formatValueType(item.type)}
                        </Text>
                      )}
                    </Flex>
                  </TableCell>
                  {!isDocument && (
                    <TableCell className="align-top">
                      <Text level="small" color="muted">
                        {formatValueType(item.type)}
                      </Text>
                    </TableCell>
                  )}
                  <TableCell
                    className={cn(
                      'max-w-md whitespace-normal align-top',
                      isDocument && 'px-4 py-3',
                    )}
                  >
                    <Text level="small">{item.description}</Text>
                  </TableCell>
                  {hasValueColumn && (
                    <TableCell
                      className={cn('align-top', isDocument && 'px-4 py-3')}
                      data-testid={`manifest-io-value-${item.name}`}
                    >
                      {value != null && item.name ? (
                        <ValueChip name={item.name} value={value} />
                      ) : isDocument &&
                        hasEditableInputs &&
                        inputValues &&
                        onInputValuesChange &&
                        item.name ? (
                        <DocumentInputValue
                          name={item.name}
                          type={item.type}
                          inputValues={inputValues}
                          onInputValuesChange={onInputValuesChange}
                          defaultInputKeys={defaultInputKeys}
                          onToggleDefault={onToggleDefault}
                          inputsReadOnly={inputsReadOnly}
                        />
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {isLong && (
        <Button
          variant="link"
          size="sm"
          className="mt-1 h-auto p-0"
          data-testid="manifest-io-table-toggle"
          onClick={() => setExpanded(value => !value)}
        >
          {expanded
            ? tAutomation('automation.manifestSopView.showLess')
            : tAutomation('automation.manifestSopView.showMore')}
        </Button>
      )}
    </Flex>
  );
}
